// components/calendar/AppointmentDetailPanel.js
// Pannello dettaglio appuntamento stile Teamleader

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
  X, ChevronLeft, ChevronRight, Edit2, MoreVertical, 
  Clock, MapPin, User, Building, FileText, Users, 
  Calendar, Phone, Mail, ExternalLink, Navigation, AlertCircle
} from 'lucide-react';
import { buildCalendarEventTitle, getAppointmentVenue } from './calendarEventFormatter';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { appointmentApi } from '../../lib/api';

// Colori per tipo appuntamento
const TYPE_COLORS = {
  sopralluogo: 'bg-[#4A90A4]',
  sede: 'bg-[#5B9BD5]',
  presentazione: 'bg-[#5B9BD5]',
  scelta_extra: 'bg-[#70AD47]',
  personale: 'bg-[#7F7F7F]',
  interno: 'bg-[#7F7F7F]',
};

const TYPE_LABELS = {
  sopralluogo: 'Sopralluogo',
  sede: 'Incontro in Sede',
  presentazione: 'Presentazione',
  scelta_extra: 'Scelta Extra',
  personale: 'Personale',
  interno: 'Interno',
};

const AppointmentDetailPanel = ({
  appointment,
  lead,
  technician,
  participants = [],
  allAppointments = [],
  onClose,
  onEdit,
  onCancel,
  onReschedule,
  onNavigatePrev,
  onNavigateNext,
  onNavigateToLead,
}) => {
  const [travelInfo, setTravelInfo] = useState(null);
  const [loadingTravel, setLoadingTravel] = useState(false);

  // Load travel info when appointment changes
  useEffect(() => {
    if (appointment?.id && appointment.source !== 'google') {
      loadTravelInfo();
    }
  }, [appointment?.id]);

  const loadTravelInfo = async () => {
    if (!appointment?.id) return;
    setLoadingTravel(true);
    try {
      const response = await appointmentApi.getTravelInfo(appointment.id);
      setTravelInfo(response.data);
    } catch (error) {
      console.warn('Could not load travel info:', error);
      setTravelInfo({ configured: false, error: 'Impossibile caricare info viaggio' });
    } finally {
      setLoadingTravel(false);
    }
  };

  if (!appointment) return null;

  const aptDate = new Date(appointment.appointment_date);
  const duration = appointment.duration_minutes || 60;
  const durationHours = Math.floor(duration / 60);
  const durationMins = duration % 60;
  const durationText = durationHours > 0 
    ? `${durationHours}h${durationMins > 0 ? durationMins.toString().padStart(2, '0') : '00'}`
    : `${durationMins} min`;

  // Titolo canonico (utility condivisa: venue snapshot-aware)
  const buildTitle = () => buildCalendarEventTitle(appointment, lead);

  // Località canonica: per fornitore/showroom usa il VENUE snapshot (nome + indirizzo/città),
  // non l'indirizzo del cliente. Per sopralluogo usa la proprietà del lead.
  const buildAddress = () => {
    const venue = getAppointmentVenue(appointment, lead);
    if (venue) {
      const parts = [venue.name, venue.address].filter(Boolean);
      if (!venue.address && venue.city) parts.push(venue.city);
      if (parts.length) return parts.join(', ');
    }
    if (!lead) return appointment.location || 'Sede ABITALO';

    const parts = [];
    if (lead.address) parts.push(lead.address);
    if (lead.city) parts.push(lead.city);
    if (lead.cap) parts.push(lead.cap);

    return parts.length > 0 ? parts.join(', ') : 'Sede ABITALO';
  };

  const address = buildAddress();
  const mapsUrl = address !== 'Sede ABITALO' 
    ? `https://maps.google.com/?q=${encodeURIComponent(address)}`
    : null;

  // Trova indice per navigazione
  const currentIndex = allAppointments.findIndex(a => a.id === appointment.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allAppointments.length - 1;

  // Iniziali per avatar
  const getInitials = (firstName, lastName) => {
    return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-xl z-50 flex flex-col border-l border-[#DCDCDC]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC] bg-[#FAFAFA]">
        <button 
          onClick={onClose}
          className="flex items-center gap-1 text-[#0066B3] hover:text-[#004C8C] text-sm font-medium"
        >
          <ChevronLeft className="w-4 h-4" />
          Panoramica incontri
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => hasPrev && onNavigatePrev?.(allAppointments[currentIndex - 1])}
            disabled={!hasPrev}
            className={`text-sm ${hasPrev ? 'text-[#737373] hover:text-[#3C3C3C]' : 'text-[#D9D9D9] cursor-not-allowed'}`}
          >
            ‹ Incontro precedente
          </button>
          <span className="text-[#D9D9D9]">|</span>
          <button
            onClick={() => hasNext && onNavigateNext?.(allAppointments[currentIndex + 1])}
            disabled={!hasNext}
            className={`text-sm ${hasNext ? 'text-[#737373] hover:text-[#3C3C3C]' : 'text-[#D9D9D9] cursor-not-allowed'}`}
          >
            Prossimo incontro ›
          </button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Info Incontro Card */}
          <div className="bg-white border border-[#DCDCDC] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC]">
              <h3 className="font-semibold text-[#3C3C3C]">Info incontro</h3>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => onEdit?.(appointment)}>
                  <Edit2 className="w-4 h-4 text-[#737373]" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="w-4 h-4 text-[#737373]" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onReschedule?.(appointment)}>
                      Riprogramma
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => onCancel?.(appointment)}
                      className="text-red-600"
                    >
                      Annulla appuntamento
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="p-4">
              {/* Titolo con badge tipo */}
              <div className="flex items-start gap-2 mb-4">
                <div className={`w-1 h-6 rounded-full ${TYPE_COLORS[appointment.appointment_type] || 'bg-gray-400'}`} />
                <h4 className="text-lg font-medium text-[#3C3C3C]">{buildTitle()}</h4>
              </div>

              {/* Grid info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {/* Colonna sinistra */}
                <div className="space-y-3">
                  <div>
                    <span className="text-[#737373] block mb-0.5">Data</span>
                    <span className="text-[#3C3C3C]">
                      {format(aptDate, "EEEE dd/MM/yyyy HH'h'mm", { locale: it })}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#737373] block mb-0.5">Durata</span>
                    <span className="text-[#3C3C3C]">{durationText}</span>
                  </div>
                  {lead && (
                    <>
                      <div>
                        <span className="text-[#737373] block mb-0.5">Cliente</span>
                        <button 
                          onClick={() => onNavigateToLead?.(lead.id)}
                          className="text-[#0066B3] hover:underline"
                        >
                          {lead.first_name} {lead.last_name}
                        </button>
                      </div>
                      <div>
                        <span className="text-[#737373] block mb-0.5">Località</span>
                        {mapsUrl ? (
                          <a 
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0066B3] hover:underline flex items-start gap-1"
                          >
                            <span>{address}</span>
                            <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          </a>
                        ) : (
                          <span className="text-[#3C3C3C]">{address}</span>
                        )}
                      </div>
                      <div>
                        <span className="text-[#737373] block mb-0.5">Opportunità</span>
                        <button 
                          onClick={() => onNavigateToLead?.(lead.id)}
                          className="text-[#0066B3] hover:underline"
                        >
                          Ristrutturazione - {lead.city || 'N/D'} - {lead.first_name} {lead.last_name}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Colonna destra */}
                <div className="space-y-3">
                  {appointment.notes && (
                    <div>
                      <span className="text-[#737373] block mb-0.5">Descrizione</span>
                      <span className="text-[#3C3C3C] whitespace-pre-wrap">{appointment.notes}</span>
                    </div>
                  )}
                  {appointment.status && (
                    <div>
                      <span className="text-[#737373] block mb-0.5">Stato</span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        appointment.status === 'confermato' ? 'bg-green-100 text-green-800' :
                        appointment.status === 'annullato' ? 'bg-red-100 text-red-800' :
                        appointment.status === 'effettuato' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {appointment.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                  {appointment.google_event_id && (
                    <div>
                      <span className="text-[#737373] block mb-0.5">Google Calendar</span>
                      <span className="text-green-600 text-xs flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        Sincronizzato
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Colleghi Partecipanti */}
          <div className="bg-white border border-[#DCDCDC] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC]">
              <h3 className="font-semibold text-[#3C3C3C]">Colleghi partecipanti</h3>
              <Button variant="ghost" size="icon">
                <Edit2 className="w-4 h-4 text-[#737373]" />
              </Button>
            </div>
            <div className="p-4">
              {technician ? (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#4A90A4] flex items-center justify-center text-white font-medium">
                    {getInitials(technician.first_name, technician.last_name)}
                  </div>
                  <div>
                    <div className="font-medium text-[#3C3C3C]">
                      {technician.first_name} {technician.last_name}
                    </div>
                    <div className="text-sm text-[#737373]">
                      {technician.role === 'external_technician' ? 'Tecnico' : 
                       technician.role === 'project_manager' ? 'Project Manager' :
                       technician.role}
                    </div>
                    {technician.email && (
                      <a href={`mailto:${technician.email}`} className="text-sm text-[#0066B3] hover:underline">
                        {technician.email}
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#737373]">Nessun tecnico assegnato</p>
              )}
            </div>
          </div>

          {/* Contatti Partecipanti (Cliente) */}
          {lead && (
            <div className="bg-white border border-[#DCDCDC] rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC]">
                <h3 className="font-semibold text-[#3C3C3C]">Contatti partecipanti</h3>
              </div>
              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#70AD47] flex items-center justify-center text-white font-medium">
                    {getInitials(lead.first_name, lead.last_name)}
                  </div>
                  <div className="flex-1">
                    <button 
                      onClick={() => onNavigateToLead?.(lead.id)}
                      className="font-medium text-[#0066B3] hover:underline"
                    >
                      {lead.first_name} {lead.last_name}
                    </button>
                    <div className="text-sm text-[#737373] mt-1 space-y-0.5">
                      {lead.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          <a href={`tel:${lead.phone}`} className="text-[#0066B3] hover:underline">
                            {lead.phone}
                          </a>
                        </div>
                      )}
                      {lead.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          <a href={`mailto:${lead.email}`} className="text-[#0066B3] hover:underline">
                            {lead.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Partecipanti Aggiuntivi (Interni + Esterni) */}
          {((appointment.participants_internal && appointment.participants_internal.length > 0) || 
            (appointment.participants_external && appointment.participants_external.length > 0)) && (
            <div className="bg-white border border-[#DCDCDC] rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC]">
                <h3 className="font-semibold text-[#3C3C3C] flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#4A90A4]" />
                  Partecipanti Aggiuntivi
                </h3>
                <span className="text-xs text-[#737373] bg-[#F5F5F5] px-2 py-0.5 rounded">
                  {(appointment.participants_internal?.length || 0) + (appointment.participants_external?.length || 0)}
                </span>
              </div>
              <div className="p-4 space-y-4">
                {/* Colleghi Interni */}
                {appointment.participants_internal && appointment.participants_internal.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs text-[#737373] uppercase tracking-wide font-medium">Colleghi</h4>
                    {appointment.participants_internal.map((p, idx) => (
                      <div key={p.user_id || idx} className="flex items-start gap-3 bg-blue-50/50 rounded-lg p-3">
                        <div className="w-8 h-8 rounded-full bg-[#5B9BD5] flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                          {getInitials(p.name?.split(' ')[0], p.name?.split(' ')[1])}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#3C3C3C] text-sm">{p.name}</span>
                            {p.role && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                {p.role === 'project_manager' ? 'PM' : 
                                 p.role === 'external_technician' ? 'Tecnico' :
                                 p.role === 'admin' ? 'Admin' : p.role}
                              </span>
                            )}
                          </div>
                          {p.email && (
                            <a href={`mailto:${p.email}`} className="text-xs text-[#0066B3] hover:underline block mt-0.5 truncate">
                              {p.email}
                            </a>
                          )}
                          {/* Badge notifiche */}
                          <div className="flex items-center gap-2 mt-2">
                            {p.send_email ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                <Mail className="w-3 h-3" />
                                Email richiesta
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                                <Mail className="w-3 h-3" />
                                No email
                              </span>
                            )}
                            {p.add_to_google && (
                              <span className="inline-flex items-center gap-1 text-xs bg-[#4285F4]/10 text-[#4285F4] px-1.5 py-0.5 rounded">
                                <Calendar className="w-3 h-3" />
                                Google Calendar
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Partecipanti Esterni / Manuali */}
                {appointment.participants_external && appointment.participants_external.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs text-[#737373] uppercase tracking-wide font-medium">Altri Partecipanti</h4>
                    {appointment.participants_external.map((p, idx) => (
                      <div key={p.email || idx} className="flex items-start gap-3 bg-green-50/50 rounded-lg p-3">
                        <div className="w-8 h-8 rounded-full bg-[#70AD47] flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                          {getInitials(p.name?.split(' ')[0], p.name?.split(' ')[1])}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#3C3C3C] text-sm">{p.name}</span>
                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                              {p.type === 'manual' ? 'Manuale' : 'Contatto'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-[#737373]">
                            {p.email && (
                              <a href={`mailto:${p.email}`} className="text-[#0066B3] hover:underline flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                {p.email}
                              </a>
                            )}
                            {p.phone && (
                              <a href={`tel:${p.phone}`} className="text-[#0066B3] hover:underline flex items-center gap-1">
                                <Phone className="w-3 h-3 flex-shrink-0" />
                                {p.phone}
                              </a>
                            )}
                          </div>
                          {/* Badge notifiche */}
                          <div className="flex items-center gap-2 mt-2">
                            {p.send_email && p.email ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                <Mail className="w-3 h-3" />
                                Email richiesta
                              </span>
                            ) : !p.email ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                <AlertCircle className="w-3 h-3" />
                                Email mancante
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                                <Mail className="w-3 h-3" />
                                No email
                              </span>
                            )}
                            {p.add_to_google && p.email && (
                              <span className="inline-flex items-center gap-1 text-xs bg-[#4285F4]/10 text-[#4285F4] px-1.5 py-0.5 rounded">
                                <Calendar className="w-3 h-3" />
                                Google Calendar
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Nota sullo stato email nei log */}
                <p className="text-xs text-[#ACACAC] mt-2 italic">
                  Lo stato invio email (sent/skipped/failed) è disponibile nei log email CRM.
                </p>
              </div>
            </div>
          )}

          {/* Info Viaggio — mostrato per tutti i tipi con luogo fisico (non personale/interno/google) */}
          {!['personale', 'interno', 'google_external'].includes(appointment.appointment_type) && appointment.source !== 'google' && (
            <div className="bg-white border border-[#DCDCDC] rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC]">
                <h3 className="font-semibold text-[#3C3C3C] flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-[#4A90A4]" />
                  Info Viaggio
                </h3>
              </div>
              <div className="p-4">
                {loadingTravel ? (
                  <div className="flex items-center gap-2 text-sm text-[#737373]">
                    <div className="w-4 h-4 border-2 border-[#4A90A4] border-t-transparent rounded-full animate-spin" />
                    Calcolo distanza...
                  </div>
                ) : !travelInfo ? (
                  <div className="flex items-start gap-3 text-sm">
                    <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#737373]">Caricamento info viaggio...</p>
                    </div>
                  </div>
                ) : travelInfo.configured ? (
                    <div className="space-y-3 text-sm">
                      {/* Origine e Destinazione */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-[#737373] block mb-0.5">Da</span>
                          <span className="text-[#3C3C3C]">{travelInfo.origin || 'Sede ABITALO'}</span>
                        </div>
                        <div>
                          <span className="text-[#737373] block mb-0.5">A</span>
                          <span className="text-[#3C3C3C]">{travelInfo.destination || address}</span>
                        </div>
                      </div>
                      
                      {/* Distanza e Durata */}
                      {travelInfo.distance_text && travelInfo.duration_text ? (
                        <>
                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[#EFEFEF]">
                          <div>
                            <span className="text-[#737373] block mb-0.5">Distanza</span>
                            <span className="text-[#3C3C3C] font-medium">{travelInfo.distance_text}</span>
                          </div>
                          <div>
                            <span className="text-[#737373] block mb-0.5">Tempo stimato</span>
                            <span className="text-[#3C3C3C] font-medium">{travelInfo.duration_text}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-[#737373]">Modalità: <span className="text-[#3C3C3C]">Auto</span></span>
                          {(travelInfo.directions_url || mapsUrl) && (
                            <a href={travelInfo.directions_url || mapsUrl} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-1 text-[#0066B3] hover:underline">
                              <MapPin className="w-3 h-3" /> Apri in Google Maps <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        </>
                      ) : (travelInfo.error || travelInfo.message) ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded">
                          <AlertCircle className="w-4 h-4" />
                          <span>{travelInfo.error || travelInfo.message}</span>
                        </div>
                      ) : null}
                      
                      {/* Orario partenza consigliato */}
                      {travelInfo.suggested_departure && (
                        <div className="pt-2 border-t border-[#EFEFEF]">
                          <span className="text-[#737373] block mb-0.5">Partenza consigliata</span>
                          <span className="text-[#4A90A4] font-medium flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {format(new Date(travelInfo.suggested_departure), "HH:mm", { locale: it })}
                            <span className="text-[#737373] font-normal ml-1">(10 min di margine inclusi)</span>
                          </span>
                        </div>
                      )}
                    </div>
                ) : (
                    /* Fallback: API non configurata */
                    <div className="flex items-start gap-3 text-sm">
                      <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[#3C3C3C] font-medium">Distanza non disponibile</p>
                        <p className="text-[#737373] mt-1">
                          {travelInfo.message || 'Servizio mappe non configurato. Contatta l\'amministratore.'}
                        </p>
                        {(travelInfo.directions_url || mapsUrl) && (
                          <a 
                            href={travelInfo.directions_url || mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-[#0066B3] hover:underline"
                          >
                            <MapPin className="w-3 h-3" />
                            Apri in Google Maps
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                )}
              </div>
            </div>
          )}

          {/* Storico / Note */}
          <div className="bg-white border border-[#DCDCDC] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#DCDCDC]">
              <h3 className="font-semibold text-[#3C3C3C]">Storico</h3>
            </div>
            <div className="p-4">
              <div className="text-sm text-[#737373]">
                <div className="flex items-start gap-3">
                  <Users className="w-4 h-4 mt-0.5 text-[#ACACAC]" />
                  <div>
                    <span className="font-medium text-[#3C3C3C]">Incontro</span>
                    <p className="mt-0.5">
                      Appuntamento pianificato: {buildTitle()}
                    </p>
                    <p className="text-xs text-[#ACACAC] mt-1">
                      {appointment.created_at 
                        ? format(new Date(appointment.created_at), "dd/MM/yyyy HH:mm", { locale: it })
                        : format(aptDate, "dd/MM/yyyy HH:mm", { locale: it })
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Footer con azioni */}
      <div className="p-4 border-t border-[#DCDCDC] bg-[#FAFAFA]">
        <Button 
          variant="outline" 
          className="w-full"
          onClick={onClose}
        >
          Chiudi
        </Button>
      </div>
    </div>
  );
};

export default AppointmentDetailPanel;
